import fetch from 'cross-fetch'
import { v4 as uuid } from 'uuid'
import Cookies from 'js-cookie'
import { IdGetterObj, InMemoryCache } from '@apollo/client/cache'
import { getMainDefinition } from '@apollo/client/utilities'
import { ApolloClient, from, HttpLink, split } from '@apollo/client/core'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import { WebSocketLink } from '@apollo/client/link/ws'
import { RetryLink } from '@apollo/client/link/retry'
import { setContext } from '@apollo/client/link/context'
import { COOKIES_TOKEN_KEY } from '@/constants'

interface Options {
  apiUrl: string
  wsUrl: string
}

interface CustomIdGetterObj extends IdGetterObj {
  __typename?: string // eslint-disable-line
  _id?: string // eslint-disable-line
  spid?: string
  uuid?: string
}

export const createApolloClient = ({ apiUrl, wsUrl }: Options) => {

  const traceTokenLink = setContext(async (_, previousContext) => ({
    headers: {
      ...previousContext.headers,
      'x-sm-trace-token': uuid(),
    },
  }))

  const httpLink = new HttpLink({
    uri: apiUrl,
    fetch,
  })

  const wsLink = new WebSocketLink(
    new SubscriptionClient(wsUrl, {
      reconnect: true,
      reconnectionAttempts: 10,
      // SS-7051: connect lazily (on first subscription, which is post-login on the MFA
      // page) so the AUTH_X_TOKEN cookie exists by connect time, and pass it explicitly
      // via connectionParams rather than relying on the cookie being on the WS handshake.
      // Eager connect at app bootstrap opened the socket before login → no cookie → the
      // mobile-beef subscription guard rejected it (worked only after a refresh).
      lazy: true,
      connectionParams: () => ({
        authToken: Cookies.get(COOKIES_TOKEN_KEY),
      }),
    }),
  )

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition'
        && definition.operation === 'subscription'
      )
    },
    wsLink,
    httpLink,
  )

  const retryLink = new RetryLink({
    delay: {
      initial: 100,
      max: 5000,
      jitter: true,
    },
    attempts: (count, operation, error) => {

      if (count > 2) {
        return false
      }

      if (typeof error.statusCode === 'number' && error.statusCode >= 500) {
        // Handle 50x error response
        return true
      }

      if (error.type === 'error' && error.statusCode === undefined) {
        // Handle progress event failure. Happens on ERR_CONNECTION_REFUSED
        return true
      }

      return false
    },
  })

  return new ApolloClient({
    link: from([
      traceTokenLink,
      retryLink,
      splitLink,
    ]),
    cache: new InMemoryCache({
      dataIdFromObject: (obj: CustomIdGetterObj) => {
        const uniqueID = obj.spid || obj.uuid || obj.id || obj._id
        if (typeof obj.__typename === 'string' && uniqueID) {
          return `${obj.__typename}:${uniqueID}`
        }
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      },
      mutate: {
        errorPolicy: 'none',
      },
    },
  })
}
